// src/components/SettingsTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  ListGroup,
  Alert,
  Spinner,
  Badge,
  Tab,
  Tabs,
  InputGroup,
  Modal,
  Accordion,
  ProgressBar
} from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const SettingsTab = () => {
  const { user, updateUser } = useAuth();
  const [activeKey, setActiveKey] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saveStatus, setSaveStatus] = useState({ saving: false, progress: 0 });

  // Состояния для разных разделов настроек
  const [profileSettings, setProfileSettings] = useState({
    name: '',
    email: '',
    phone: '',
    language: 'ru',
    timezone: 'Europe/Moscow'
  });

  const [notificationSettings, setNotificationSettings] = useState({
    email: {
      tasks: true,
      comments: true,
      mentions: true,
      statusChanges: true,
      reminders: true,
      weeklyDigest: false
    },
    telegram: {
      tasks: true,
      comments: true,
      mentions: true,
      statusChanges: true,
      reminders: true,
      broadcast: true
    },
    web: {
      tasks: true,
      comments: true,
      mentions: true,
      statusChanges: true,
      reminders: true,
      sound: true
    },
    reminderSettings: {
      dueSoon: '1h',
      overdue: '1h',
      statusChange: true,
      newComments: true
    }
  });

  const [taskSettings, setTaskSettings] = useState({
    defaultView: 'grid',
    defaultSort: 'created_at',
    defaultSortOrder: 'desc',
    tasksPerPage: 15,
    showOverdueFirst: true,
    autoRefresh: true,
    refreshInterval: 30,
    showEstimatedTime: true,
    showActualTime: true,
    compactView: false
  });

  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'light',
    themeColor: 'blue',
    fontSize: 'medium',
    density: 'comfortable',
    animations: true,
    showAvatars: true,
    showBadges: true,
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    weekStart: 'monday'
  });

  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'team',
    showOnlineStatus: true,
    showLastSeen: true,
    allowTaskAssignments: true,
    allowDirectMessages: true,
    showEmail: 'team',
    showPhone: 'none',
    dataSharing: {
      analytics: true,
      improveSystem: true,
      marketing: false
    }
  });

  const [securitySettings, setSecuritySettings] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
    twoFactorMethod: 'telegram',
    sessionTimeout: 60,
    loginNotifications: true,
    loginFromNewDevice: true
  });

  const [integrationSettings, setIntegrationSettings] = useState({
    telegram: {
      connected: false,
      username: '',
      notifications: true,
      sendReadReceipts: true
    },
    googleCalendar: {
      connected: false,
      syncTasks: false,
      syncDirection: 'both'
    },
    api: {
      enabled: false,
      apiKey: '',
      lastUsed: null,
      permissions: ['read:tasks', 'write:tasks']
    }
  });

  // Загрузка сохранённых настроек при монтировании
  useEffect(() => {
    loadSavedSettings();
  }, []);

  const loadSavedSettings = () => {
    try {
      // Профиль
      const savedProfile = localStorage.getItem('settings_profile');
      if (savedProfile) {
        setProfileSettings(JSON.parse(savedProfile));
      } else if (user) {
        setProfileSettings({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          language: 'ru',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow'
        });
      }

      // Уведомления
      const savedNotifications = localStorage.getItem('settings_notifications');
      if (savedNotifications) {
        setNotificationSettings(JSON.parse(savedNotifications));
      }

      // Задачи
      const savedTasks = localStorage.getItem('settings_tasks');
      if (savedTasks) {
        setTaskSettings(JSON.parse(savedTasks));
      }

      // Внешний вид
      const savedAppearance = localStorage.getItem('settings_appearance');
      if (savedAppearance) {
        const appearance = JSON.parse(savedAppearance);
        setAppearanceSettings(appearance);
        // Применяем тему сразу
        applyTheme(appearance.theme);
      }

      // Приватность
      const savedPrivacy = localStorage.getItem('settings_privacy');
      if (savedPrivacy) {
        setPrivacySettings(JSON.parse(savedPrivacy));
      }

      // Безопасность
      const savedSecurity = localStorage.getItem('settings_security');
      if (savedSecurity) {
        setSecuritySettings(JSON.parse(savedSecurity));
      }

      // Интеграции
      const savedIntegrations = localStorage.getItem('settings_integrations');
      if (savedIntegrations) {
        setIntegrationSettings(JSON.parse(savedIntegrations));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const applyTheme = (theme) => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem('bsTheme', theme);
  };

  // Обработчики изменений для разных разделов
  const handleProfileChange = (field, value) => {
    setProfileSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationChange = (category, field, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleTaskSettingChange = (field, value) => {
    setTaskSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleAppearanceChange = (field, value) => {
    const newSettings = { ...appearanceSettings, [field]: value };
    setAppearanceSettings(newSettings);
    
    if (field === 'theme') {
      applyTheme(value);
    }
  };

  const handlePrivacyChange = (field, value) => {
    setPrivacySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSecurityChange = (field, value) => {
    setSecuritySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleIntegrationChange = (category, field, value) => {
    setIntegrationSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Сохранение настроек
  const saveSettings = async (category) => {
    setSaveStatus({ saving: true, progress: 0 });
    
    try {
      // Имитация прогресса сохранения
      const progressInterval = setInterval(() => {
        setSaveStatus(prev => {
          if (prev.progress >= 90) {
            clearInterval(progressInterval);
            return { ...prev, progress: 90 };
          }
          return { ...prev, progress: prev.progress + 10 };
        });
      }, 50);

      switch (category) {
        case 'profile':
          localStorage.setItem('settings_profile', JSON.stringify(profileSettings));
          if (user) {
            await updateUser({
              name: profileSettings.name,
              email: profileSettings.email,
              phone: profileSettings.phone
            });
          }
          break;
        case 'notifications':
          localStorage.setItem('settings_notifications', JSON.stringify(notificationSettings));
          break;
        case 'tasks':
          localStorage.setItem('settings_tasks', JSON.stringify(taskSettings));
          break;
        case 'appearance':
          localStorage.setItem('settings_appearance', JSON.stringify(appearanceSettings));
          break;
        case 'privacy':
          localStorage.setItem('settings_privacy', JSON.stringify(privacySettings));
          break;
        case 'security':
          localStorage.setItem('settings_security', JSON.stringify(securitySettings));
          break;
        case 'integrations':
          localStorage.setItem('settings_integrations', JSON.stringify(integrationSettings));
          break;
        case 'all':
          // Сохраняем все настройки
          localStorage.setItem('settings_profile', JSON.stringify(profileSettings));
          localStorage.setItem('settings_notifications', JSON.stringify(notificationSettings));
          localStorage.setItem('settings_tasks', JSON.stringify(taskSettings));
          localStorage.setItem('settings_appearance', JSON.stringify(appearanceSettings));
          localStorage.setItem('settings_privacy', JSON.stringify(privacySettings));
          localStorage.setItem('settings_security', JSON.stringify(securitySettings));
          localStorage.setItem('settings_integrations', JSON.stringify(integrationSettings));
          break;
      }

      clearInterval(progressInterval);
      setSaveStatus({ saving: true, progress: 100 });

      setMessage({
        type: 'success',
        text: 'Настройки успешно сохранены!'
      });

      // Сбрасываем прогресс через секунду
      setTimeout(() => {
        setSaveStatus({ saving: false, progress: 0 });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }, 1000);

    } catch (error) {
      setMessage({
        type: 'danger',
        text: 'Ошибка при сохранении настроек'
      });
      setSaveStatus({ saving: false, progress: 0 });
    }
  };

  // Сброс настроек к значениям по умолчанию
  const resetSettings = (category) => {
    if (window.confirm('Вы уверены, что хотите сбросить настройки? Все изменения будут потеряны.')) {
      switch (category) {
        case 'profile':
          setProfileSettings({
            name: user?.name || '',
            email: user?.email || '',
            phone: '',
            language: 'ru',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow'
          });
          break;
        case 'notifications':
          setNotificationSettings({
            email: {
              tasks: true,
              comments: true,
              mentions: true,
              statusChanges: true,
              reminders: true,
              weeklyDigest: false
            },
            telegram: {
              tasks: true,
              comments: true,
              mentions: true,
              statusChanges: true,
              reminders: true,
              broadcast: true
            },
            web: {
              tasks: true,
              comments: true,
              mentions: true,
              statusChanges: true,
              reminders: true,
              sound: true
            },
            reminderSettings: {
              dueSoon: '1h',
              overdue: '1h',
              statusChange: true,
              newComments: true
            }
          });
          break;
        case 'tasks':
          setTaskSettings({
            defaultView: 'grid',
            defaultSort: 'created_at',
            defaultSortOrder: 'desc',
            tasksPerPage: 15,
            showOverdueFirst: true,
            autoRefresh: true,
            refreshInterval: 30,
            showEstimatedTime: true,
            showActualTime: true,
            compactView: false
          });
          break;
        case 'appearance':
          setAppearanceSettings({
            theme: 'light',
            themeColor: 'blue',
            fontSize: 'medium',
            density: 'comfortable',
            animations: true,
            showAvatars: true,
            showBadges: true,
            dateFormat: 'DD.MM.YYYY',
            timeFormat: '24h',
            weekStart: 'monday'
          });
          applyTheme('light');
          break;
        case 'privacy':
          setPrivacySettings({
            profileVisibility: 'team',
            showOnlineStatus: true,
            showLastSeen: true,
            allowTaskAssignments: true,
            allowDirectMessages: true,
            showEmail: 'team',
            showPhone: 'none',
            dataSharing: {
              analytics: true,
              improveSystem: true,
              marketing: false
            }
          });
          break;
        case 'security':
          setSecuritySettings({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            twoFactorEnabled: false,
            twoFactorMethod: 'telegram',
            sessionTimeout: 60,
            loginNotifications: true,
            loginFromNewDevice: true
          });
          break;
        case 'integrations':
          setIntegrationSettings({
            telegram: {
              connected: user?.telegram_username ? true : false,
              username: user?.telegram_username || '',
              notifications: true,
              sendReadReceipts: true
            },
            googleCalendar: {
              connected: false,
              syncTasks: false,
              syncDirection: 'both'
            },
            api: {
              enabled: false,
              apiKey: '',
              lastUsed: null,
              permissions: ['read:tasks', 'write:tasks']
            }
          });
          break;
        case 'all':
          // Сброс всех настроек
          loadSavedSettings();
          break;
      }

      setMessage({
        type: 'info',
        text: 'Настройки сброшены к значениям по умолчанию'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  // Экспорт/импорт настроек
  const exportSettings = () => {
    const allSettings = {
      profile: profileSettings,
      notifications: notificationSettings,
      tasks: taskSettings,
      appearance: appearanceSettings,
      privacy: privacySettings,
      security: securitySettings,
      integrations: integrationSettings,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };

    const dataStr = JSON.stringify(allSettings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `taskit-settings-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importSettings = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const settings = JSON.parse(e.target.result);
        
        if (settings.profile) setProfileSettings(settings.profile);
        if (settings.notifications) setNotificationSettings(settings.notifications);
        if (settings.tasks) setTaskSettings(settings.tasks);
        if (settings.appearance) {
          setAppearanceSettings(settings.appearance);
          if (settings.appearance.theme) {
            applyTheme(settings.appearance.theme);
          }
        }
        if (settings.privacy) setPrivacySettings(settings.privacy);
        if (settings.security) setSecuritySettings(settings.security);
        if (settings.integrations) setIntegrationSettings(settings.integrations);

        setMessage({
          type: 'success',
          text: 'Настройки успешно импортированы!'
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        setMessage({
          type: 'danger',
          text: 'Ошибка при импорте настроек: неверный формат файла'
        });
      }
    };
    reader.readAsText(file);
  };

  // Компоненты для каждого раздела настроек

  const ProfileSettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-person me-2"></i>
          Настройки профиля
        </h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Имя</Form.Label>
              <Form.Control
                type="text"
                value={profileSettings.name}
                onChange={(e) => handleProfileChange('name', e.target.value)}
                placeholder="Введите ваше имя"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={profileSettings.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                placeholder="Введите email"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Телефон</Form.Label>
              <Form.Control
                type="tel"
                value={profileSettings.phone}
                onChange={(e) => handleProfileChange('phone', e.target.value)}
                placeholder="+7 (XXX) XXX-XX-XX"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Язык интерфейса</Form.Label>
              <Form.Select
                value={profileSettings.language}
                onChange={(e) => handleProfileChange('language', e.target.value)}
              >
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Часовой пояс</Form.Label>
              <Form.Select
                value={profileSettings.timezone}
                onChange={(e) => handleProfileChange('timezone', e.target.value)}
              >
                <option value="Europe/Moscow">Москва (+3)</option>
                <option value="Europe/London">Лондон (+0)</option>
                <option value="Europe/Berlin">Берлин (+1)</option>
                <option value="America/New_York">Нью-Йорк (-5)</option>
                <option value="Asia/Tokyo">Токио (+9)</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('profile')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('profile')}>
          Сохранить профиль
        </Button>
      </Card.Footer>
    </Card>
  );

  const NotificationSettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-bell me-2"></i>
          Настройки уведомлений
        </h5>
      </Card.Header>
      <Card.Body>
        <Tabs defaultActiveKey="email" className="mb-4">
          <Tab eventKey="email" title="Email">
            <Form>
              <Form.Check
                type="checkbox"
                label="Новые задачи"
                checked={notificationSettings.email.tasks}
                onChange={(e) => handleNotificationChange('email', 'tasks', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Комментарии к задачам"
                checked={notificationSettings.email.comments}
                onChange={(e) => handleNotificationChange('email', 'comments', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Упоминания"
                checked={notificationSettings.email.mentions}
                onChange={(e) => handleNotificationChange('email', 'mentions', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Изменения статуса"
                checked={notificationSettings.email.statusChanges}
                onChange={(e) => handleNotificationChange('email', 'statusChanges', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Напоминания о сроках"
                checked={notificationSettings.email.reminders}
                onChange={(e) => handleNotificationChange('email', 'reminders', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Еженедельный дайджест"
                checked={notificationSettings.email.weeklyDigest}
                onChange={(e) => handleNotificationChange('email', 'weeklyDigest', e.target.checked)}
                className="mb-2"
              />
            </Form>
          </Tab>
          
          <Tab eventKey="telegram" title="Telegram">
            <Form>
              <Form.Check
                type="checkbox"
                label="Новые задачи"
                checked={notificationSettings.telegram.tasks}
                onChange={(e) => handleNotificationChange('telegram', 'tasks', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Комментарии"
                checked={notificationSettings.telegram.comments}
                onChange={(e) => handleNotificationChange('telegram', 'comments', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Упоминания"
                checked={notificationSettings.telegram.mentions}
                onChange={(e) => handleNotificationChange('telegram', 'mentions', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Изменения статуса"
                checked={notificationSettings.telegram.statusChanges}
                onChange={(e) => handleNotificationChange('telegram', 'statusChanges', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Напоминания"
                checked={notificationSettings.telegram.reminders}
                onChange={(e) => handleNotificationChange('telegram', 'reminders', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Рассылки администрации"
                checked={notificationSettings.telegram.broadcast}
                onChange={(e) => handleNotificationChange('telegram', 'broadcast', e.target.checked)}
                className="mb-2"
              />
            </Form>
          </Tab>
          
          <Tab eventKey="web" title="Веб-интерфейс">
            <Form>
              <Form.Check
                type="checkbox"
                label="Новые задачи"
                checked={notificationSettings.web.tasks}
                onChange={(e) => handleNotificationChange('web', 'tasks', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Комментарии"
                checked={notificationSettings.web.comments}
                onChange={(e) => handleNotificationChange('web', 'comments', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Упоминания"
                checked={notificationSettings.web.mentions}
                onChange={(e) => handleNotificationChange('web', 'mentions', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Изменения статуса"
                checked={notificationSettings.web.statusChanges}
                onChange={(e) => handleNotificationChange('web', 'statusChanges', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Напоминания"
                checked={notificationSettings.web.reminders}
                onChange={(e) => handleNotificationChange('web', 'reminders', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Звуковые уведомления"
                checked={notificationSettings.web.sound}
                onChange={(e) => handleNotificationChange('web', 'sound', e.target.checked)}
                className="mb-2"
              />
            </Form>
          </Tab>
          
          <Tab eventKey="reminders" title="Напоминания">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Напоминать о задачах со сроком:</Form.Label>
                <Form.Select
                  value={notificationSettings.reminderSettings.dueSoon}
                  onChange={(e) => handleNotificationChange('reminderSettings', 'dueSoon', e.target.value)}
                >
                  <option value="30m">За 30 минут</option>
                  <option value="1h">За 1 час</option>
                  <option value="3h">За 3 часа</option>
                  <option value="6h">За 6 часов</option>
                  <option value="12h">За 12 часов</option>
                  <option value="1d">За 1 день</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Напоминать о просроченных задачах:</Form.Label>
                <Form.Select
                  value={notificationSettings.reminderSettings.overdue}
                  onChange={(e) => handleNotificationChange('reminderSettings', 'overdue', e.target.value)}
                >
                  <option value="1h">Через 1 час</option>
                  <option value="3h">Через 3 часа</option>
                  <option value="6h">Через 6 часов</option>
                  <option value="12h">Через 12 часов</option>
                  <option value="1d">Через 1 день</option>
                  <option value="never">Не напоминать</option>
                </Form.Select>
              </Form.Group>
              
              <Form.Check
                type="checkbox"
                label="Уведомлять об изменениях статуса"
                checked={notificationSettings.reminderSettings.statusChange}
                onChange={(e) => handleNotificationChange('reminderSettings', 'statusChange', e.target.checked)}
                className="mb-2"
              />
              <Form.Check
                type="checkbox"
                label="Уведомлять о новых комментариях"
                checked={notificationSettings.reminderSettings.newComments}
                onChange={(e) => handleNotificationChange('reminderSettings', 'newComments', e.target.checked)}
                className="mb-2"
              />
            </Form>
          </Tab>
        </Tabs>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('notifications')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('notifications')}>
          Сохранить уведомления
        </Button>
      </Card.Footer>
    </Card>
  );

  const TaskSettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-list-task me-2"></i>
          Настройки задач
        </h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Вид по умолчанию</Form.Label>
              <Form.Select
                value={taskSettings.defaultView}
                onChange={(e) => handleTaskSettingChange('defaultView', e.target.value)}
              >
                <option value="grid">Сетка</option>
                <option value="table">Таблица</option>
                <option value="board">Доска</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Сортировка по умолчанию</Form.Label>
              <Form.Select
                value={taskSettings.defaultSort}
                onChange={(e) => handleTaskSettingChange('defaultSort', e.target.value)}
              >
                <option value="created_at">Дата создания</option>
                <option value="due_date">Срок выполнения</option>
                <option value="priority">Приоритет</option>
                <option value="title">Название</option>
                <option value="updated_at">Дата обновления</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Порядок сортировки</Form.Label>
              <Form.Select
                value={taskSettings.defaultSortOrder}
                onChange={(e) => handleTaskSettingChange('defaultSortOrder', e.target.value)}
              >
                <option value="desc">По убыванию (сначала новые)</option>
                <option value="asc">По возрастанию (сначала старые)</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Задач на странице</Form.Label>
              <Form.Select
                value={taskSettings.tasksPerPage}
                onChange={(e) => handleTaskSettingChange('tasksPerPage', parseInt(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <hr />
            <h6>Дополнительные настройки</h6>
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Показывать просроченные задачи первыми"
              checked={taskSettings.showOverdueFirst}
              onChange={(e) => handleTaskSettingChange('showOverdueFirst', e.target.checked)}
              className="mb-3"
            />
            
            <Form.Check
              type="checkbox"
              label="Автообновление списка задач"
              checked={taskSettings.autoRefresh}
              onChange={(e) => handleTaskSettingChange('autoRefresh', e.target.checked)}
              className="mb-3"
            />
            
            {taskSettings.autoRefresh && (
              <Form.Group className="mb-3">
                <Form.Label>Интервал обновления (секунды)</Form.Label>
                <Form.Control
                  type="number"
                  value={taskSettings.refreshInterval}
                  onChange={(e) => handleTaskSettingChange('refreshInterval', parseInt(e.target.value))}
                  min={5}
                  max={300}
                />
              </Form.Group>
            )}
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Показывать оценку времени"
              checked={taskSettings.showEstimatedTime}
              onChange={(e) => handleTaskSettingChange('showEstimatedTime', e.target.checked)}
              className="mb-3"
            />
            
            <Form.Check
              type="checkbox"
              label="Показывать фактическое время"
              checked={taskSettings.showActualTime}
              onChange={(e) => handleTaskSettingChange('showActualTime', e.target.checked)}
              className="mb-3"
            />
            
            <Form.Check
              type="checkbox"
              label="Компактный вид"
              checked={taskSettings.compactView}
              onChange={(e) => handleTaskSettingChange('compactView', e.target.checked)}
              className="mb-3"
            />
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('tasks')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('tasks')}>
          Сохранить задачи
        </Button>
      </Card.Footer>
    </Card>
  );

  const AppearanceSettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-palette me-2"></i>
          Внешний вид
        </h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Тема оформления</Form.Label>
              <Form.Select
                value={appearanceSettings.theme}
                onChange={(e) => handleAppearanceChange('theme', e.target.value)}
              >
                <option value="light">Светлая</option>
                <option value="dark">Темная</option>
                <option value="auto">Системная</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Цветовая схема</Form.Label>
              <Form.Select
                value={appearanceSettings.themeColor}
                onChange={(e) => handleAppearanceChange('themeColor', e.target.value)}
              >
                <option value="blue">Синяя</option>
                <option value="green">Зеленая</option>
                <option value="purple">Фиолетовая</option>
                <option value="red">Красная</option>
                <option value="orange">Оранжевая</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Размер шрифта</Form.Label>
              <Form.Select
                value={appearanceSettings.fontSize}
                onChange={(e) => handleAppearanceChange('fontSize', e.target.value)}
              >
                <option value="small">Маленький</option>
                <option value="medium">Средний</option>
                <option value="large">Большой</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Плотность интерфейса</Form.Label>
              <Form.Select
                value={appearanceSettings.density}
                onChange={(e) => handleAppearanceChange('density', e.target.value)}
              >
                <option value="compact">Компактная</option>
                <option value="comfortable">Комфортная</option>
                <option value="spacious">Просторная</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <hr />
            <h6>Отображение элементов</h6>
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Анимации интерфейса"
              checked={appearanceSettings.animations}
              onChange={(e) => handleAppearanceChange('animations', e.target.checked)}
              className="mb-3"
            />
            
            <Form.Check
              type="checkbox"
              label="Показывать аватары пользователей"
              checked={appearanceSettings.showAvatars}
              onChange={(e) => handleAppearanceChange('showAvatars', e.target.checked)}
              className="mb-3"
            />
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Показывать бейджи"
              checked={appearanceSettings.showBadges}
              onChange={(e) => handleAppearanceChange('showBadges', e.target.checked)}
              className="mb-3"
            />
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Формат даты</Form.Label>
              <Form.Select
                value={appearanceSettings.dateFormat}
                onChange={(e) => handleAppearanceChange('dateFormat', e.target.value)}
              >
                <option value="DD.MM.YYYY">ДД.ММ.ГГГГ</option>
                <option value="YYYY-MM-DD">ГГГГ-ММ-ДД</option>
                <option value="MM/DD/YYYY">ММ/ДД/ГГГГ</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Формат времени</Form.Label>
              <Form.Select
                value={appearanceSettings.timeFormat}
                onChange={(e) => handleAppearanceChange('timeFormat', e.target.value)}
              >
                <option value="24h">24-часовой</option>
                <option value="12h">12-часовой</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Начало недели</Form.Label>
              <Form.Select
                value={appearanceSettings.weekStart}
                onChange={(e) => handleAppearanceChange('weekStart', e.target.value)}
              >
                <option value="monday">Понедельник</option>
                <option value="sunday">Воскресенье</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('appearance')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('appearance')}>
          Сохранить внешний вид
        </Button>
      </Card.Footer>
    </Card>
  );

  const PrivacySettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-shield-lock me-2"></i>
          Приватность и безопасность
        </h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Видимость профиля</Form.Label>
              <Form.Select
                value={privacySettings.profileVisibility}
                onChange={(e) => handlePrivacyChange('profileVisibility', e.target.value)}
              >
                <option value="public">Все пользователи</option>
                <option value="team">Только команда</option>
                <option value="private">Только я</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Показывать статус онлайн</Form.Label>
              <Form.Select
                value={privacySettings.showOnlineStatus}
                onChange={(e) => handlePrivacyChange('showOnlineStatus', e.target.value === 'true')}
              >
                <option value="true">Да</option>
                <option value="false">Нет</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Показывать время последнего входа</Form.Label>
              <Form.Select
                value={privacySettings.showLastSeen}
                onChange={(e) => handlePrivacyChange('showLastSeen', e.target.value === 'true')}
              >
                <option value="true">Да</option>
                <option value="false">Нет</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Разрешить назначение задач</Form.Label>
              <Form.Select
                value={privacySettings.allowTaskAssignments}
                onChange={(e) => handlePrivacyChange('allowTaskAssignments', e.target.value === 'true')}
              >
                <option value="true">Да</option>
                <option value="false">Нет</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Разрешить личные сообщения</Form.Label>
              <Form.Select
                value={privacySettings.allowDirectMessages}
                onChange={(e) => handlePrivacyChange('allowDirectMessages', e.target.value === 'true')}
              >
                <option value="true">Да</option>
                <option value="false">Нет</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Показывать email</Form.Label>
              <Form.Select
                value={privacySettings.showEmail}
                onChange={(e) => handlePrivacyChange('showEmail', e.target.value)}
              >
                <option value="all">Всем</option>
                <option value="team">Только команде</option>
                <option value="none">Никому</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Показывать телефон</Form.Label>
              <Form.Select
                value={privacySettings.showPhone}
                onChange={(e) => handlePrivacyChange('showPhone', e.target.value)}
              >
                <option value="all">Всем</option>
                <option value="team">Только команде</option>
                <option value="none">Никому</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <hr />
            <h6>Обмен данными</h6>
          </Col>
          
          <Col md={12}>
            <Form.Check
              type="checkbox"
              label="Анонимная статистика использования"
              checked={privacySettings.dataSharing.analytics}
              onChange={(e) => handlePrivacyChange('dataSharing', {
                ...privacySettings.dataSharing,
                analytics: e.target.checked
              })}
              className="mb-3"
            />
            
            <Form.Check
              type="checkbox"
              label="Отправлять данные для улучшения системы"
              checked={privacySettings.dataSharing.improveSystem}
              onChange={(e) => handlePrivacyChange('dataSharing', {
                ...privacySettings.dataSharing,
                improveSystem: e.target.checked
              })}
              className="mb-3"
            />
            
            <Form.Check
              type="checkbox"
              label="Разрешить маркетинговые рассылки"
              checked={privacySettings.dataSharing.marketing}
              onChange={(e) => handlePrivacyChange('dataSharing', {
                ...privacySettings.dataSharing,
                marketing: e.target.checked
              })}
              className="mb-3"
            />
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('privacy')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('privacy')}>
          Сохранить приватность
        </Button>
      </Card.Footer>
    </Card>
  );

  const SecuritySettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-shield-check me-2"></i>
          Безопасность аккаунта
        </h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={12}>
            <h6>Смена пароля</h6>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Текущий пароль</Form.Label>
              <Form.Control
                type="password"
                value={securitySettings.currentPassword}
                onChange={(e) => handleSecurityChange('currentPassword', e.target.value)}
                placeholder="Введите текущий пароль"
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Новый пароль</Form.Label>
              <Form.Control
                type="password"
                value={securitySettings.newPassword}
                onChange={(e) => handleSecurityChange('newPassword', e.target.value)}
                placeholder="Введите новый пароль"
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Подтвердите новый пароль</Form.Label>
              <Form.Control
                type="password"
                value={securitySettings.confirmPassword}
                onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)}
                placeholder="Подтвердите новый пароль"
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <hr />
            <h6>Двухфакторная аутентификация</h6>
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Включить двухфакторную аутентификацию"
              checked={securitySettings.twoFactorEnabled}
              onChange={(e) => handleSecurityChange('twoFactorEnabled', e.target.checked)}
              className="mb-3"
            />
            
            {securitySettings.twoFactorEnabled && (
              <Form.Group className="mb-3">
                <Form.Label>Метод 2FA</Form.Label>
                <Form.Select
                  value={securitySettings.twoFactorMethod}
                  onChange={(e) => handleSecurityChange('twoFactorMethod', e.target.value)}
                >
                  <option value="telegram">Telegram</option>
                  <option value="email">Email</option>
                  <option value="app">Приложение аутентификатор</option>
                </Form.Select>
              </Form.Group>
            )}
          </Col>
          
          <Col md={6}>
            <Form.Group>
              <Form.Label>Таймаут сессии (минут)</Form.Label>
              <Form.Control
                type="number"
                value={securitySettings.sessionTimeout}
                onChange={(e) => handleSecurityChange('sessionTimeout', parseInt(e.target.value))}
                min={5}
                max={1440}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <hr />
            <h6>Уведомления безопасности</h6>
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Уведомления о входе в аккаунт"
              checked={securitySettings.loginNotifications}
              onChange={(e) => handleSecurityChange('loginNotifications', e.target.checked)}
              className="mb-3"
            />
          </Col>
          
          <Col md={6}>
            <Form.Check
              type="checkbox"
              label="Уведомлять о входе с нового устройства"
              checked={securitySettings.loginFromNewDevice}
              onChange={(e) => handleSecurityChange('loginFromNewDevice', e.target.checked)}
              className="mb-3"
            />
          </Col>
        </Row>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('security')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('security')}>
          Сохранить безопасность
        </Button>
      </Card.Footer>
    </Card>
  );

  const IntegrationSettingsSection = () => (
    <Card>
      <Card.Header>
        <h5 className="mb-0">
          <i className="bi bi-puzzle me-2"></i>
          Интеграции
        </h5>
      </Card.Header>
      <Card.Body>
        <Accordion defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div className="d-flex align-items-center">
                <i className="bi bi-telegram me-2"></i>
                <span>Telegram</span>
                {integrationSettings.telegram.connected && (
                  <Badge bg="success" className="ms-2">Подключен</Badge>
                )}
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {integrationSettings.telegram.connected ? (
                <div>
                  <p>
                    <strong>Подключен аккаунт:</strong> @{integrationSettings.telegram.username}
                  </p>
                  <Form.Check
                    type="checkbox"
                    label="Уведомления через Telegram"
                    checked={integrationSettings.telegram.notifications}
                    onChange={(e) => handleIntegrationChange('telegram', 'notifications', e.target.checked)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="checkbox"
                    label="Отправлять подтверждения прочтения"
                    checked={integrationSettings.telegram.sendReadReceipts}
                    onChange={(e) => handleIntegrationChange('telegram', 'sendReadReceipts', e.target.checked)}
                    className="mb-2"
                  />
                  <Button variant="outline-danger" size="sm">
                    Отключить Telegram
                  </Button>
                </div>
              ) : (
                <div>
                  <p>Подключите Telegram для получения уведомлений</p>
                  <Button variant="primary">
                    <i className="bi bi-telegram me-1"></i>
                    Подключить Telegram
                  </Button>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <div className="d-flex align-items-center">
                <i className="bi bi-google me-2"></i>
                <span>Google Calendar</span>
                {integrationSettings.googleCalendar.connected && (
                  <Badge bg="success" className="ms-2">Подключен</Badge>
                )}
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {integrationSettings.googleCalendar.connected ? (
                <div>
                  <Form.Check
                    type="checkbox"
                    label="Синхронизировать задачи с календарем"
                    checked={integrationSettings.googleCalendar.syncTasks}
                    onChange={(e) => handleIntegrationChange('googleCalendar', 'syncTasks', e.target.checked)}
                    className="mb-3"
                  />
                  
                  {integrationSettings.googleCalendar.syncTasks && (
                    <Form.Group className="mb-3">
                      <Form.Label>Направление синхронизации</Form.Label>
                      <Form.Select
                        value={integrationSettings.googleCalendar.syncDirection}
                        onChange={(e) => handleIntegrationChange('googleCalendar', 'syncDirection', e.target.value)}
                      >
                        <option value="both">Двусторонняя</option>
                        <option value="to_calendar">В календарь</option>
                        <option value="from_calendar">Из календаря</option>
                      </Form.Select>
                    </Form.Group>
                  )}
                  
                  <Button variant="outline-danger" size="sm">
                    Отключить Google Calendar
                  </Button>
                </div>
              ) : (
                <div>
                  <p>Подключите Google Calendar для синхронизации задач</p>
                  <Button variant="outline-primary">
                    <i className="bi bi-google me-1"></i>
                    Подключить Google Calendar
                  </Button>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
          
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              <div className="d-flex align-items-center">
                <i className="bi bi-key me-2"></i>
                <span>API доступ</span>
                {integrationSettings.api.enabled && (
                  <Badge bg="success" className="ms-2">Включен</Badge>
                )}
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Form.Check
                type="checkbox"
                label="Включить доступ по API"
                checked={integrationSettings.api.enabled}
                onChange={(e) => handleIntegrationChange('api', 'enabled', e.target.checked)}
                className="mb-3"
              />
              
              {integrationSettings.api.enabled && (
                <div>
                  <Form.Group className="mb-3">
                    <Form.Label>API ключ</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={integrationSettings.api.apiKey}
                        readOnly
                        placeholder="Сгенерируйте API ключ"
                      />
                      <Button variant="outline-secondary">
                        <i className="bi bi-copy"></i>
                      </Button>
                      <Button variant="outline-secondary">
                        <i className="bi bi-arrow-clockwise"></i>
                      </Button>
                    </InputGroup>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Разрешения API</Form.Label>
                    <div>
                      {integrationSettings.api.permissions.map((perm, index) => (
                        <Badge key={index} bg="info" className="me-1 mb-1">
                          {perm}
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>
                  
                  {integrationSettings.api.lastUsed && (
                    <p className="text-muted small">
                      <i className="bi bi-clock-history me-1"></i>
                      Последнее использование: {new Date(integrationSettings.api.lastUsed).toLocaleString('ru-RU')}
                    </p>
                  )}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={() => resetSettings('integrations')}>
          Сбросить
        </Button>
        <Button variant="primary" onClick={() => saveSettings('integrations')}>
          Сохранить интеграции
        </Button>
      </Card.Footer>
    </Card>
  );

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-0">
            <i className="bi bi-gear me-2"></i>
            Настройки
          </h1>
          <p className="text-muted mb-0">
            Настройте систему под свои предпочтения
          </p>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <div className="btn-group">
            <Button variant="outline-primary" onClick={exportSettings}>
              <i className="bi bi-download me-1"></i>
              Экспорт
            </Button>
            <Button variant="outline-primary" as="label" htmlFor="importSettings">
              <i className="bi bi-upload me-1"></i>
              Импорт
              <input
                type="file"
                id="importSettings"
                accept=".json"
                onChange={importSettings}
                style={{ display: 'none' }}
              />
            </Button>
            <Button variant="outline-danger" onClick={() => resetSettings('all')}>
              <i className="bi bi-arrow-clockwise me-1"></i>
              Сбросить все
            </Button>
          </div>
        </Col>
      </Row>

      {message.text && (
        <Alert variant={message.type} className="mb-4" dismissible onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {saveStatus.saving && (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-3" />
              <div className="flex-grow-1">
                <ProgressBar now={saveStatus.progress} label={`${saveStatus.progress}%`} />
                <small className="text-muted">Сохранение настроек...</small>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Row>
        <Col lg={3}>
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item
                  action
                  active={activeKey === 'profile'}
                  onClick={() => setActiveKey('profile')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-person me-3"></i>
                  Профиль
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  active={activeKey === 'notifications'}
                  onClick={() => setActiveKey('notifications')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-bell me-3"></i>
                  Уведомления
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  active={activeKey === 'tasks'}
                  onClick={() => setActiveKey('tasks')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-list-task me-3"></i>
                  Задачи
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  active={activeKey === 'appearance'}
                  onClick={() => setActiveKey('appearance')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-palette me-3"></i>
                  Внешний вид
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  active={activeKey === 'privacy'}
                  onClick={() => setActiveKey('privacy')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-shield-lock me-3"></i>
                  Приватность
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  active={activeKey === 'security'}
                  onClick={() => setActiveKey('security')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-shield-check me-3"></i>
                  Безопасность
                </ListGroup.Item>
                <ListGroup.Item
                  action
                  active={activeKey === 'integrations'}
                  onClick={() => setActiveKey('integrations')}
                  className="d-flex align-items-center"
                >
                  <i className="bi bi-puzzle me-3"></i>
                  Интеграции
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
            <Card.Footer className="text-center">
              <Button 
                variant="success" 
                className="w-100"
                onClick={() => saveSettings('all')}
                disabled={saveStatus.saving}
              >
                <i className="bi bi-check-lg me-1"></i>
                Сохранить все настройки
              </Button>
            </Card.Footer>
          </Card>
        </Col>

        <Col lg={9}>
          <div style={{ minHeight: '500px' }}>
            {activeKey === 'profile' && <ProfileSettingsSection />}
            {activeKey === 'notifications' && <NotificationSettingsSection />}
            {activeKey === 'tasks' && <TaskSettingsSection />}
            {activeKey === 'appearance' && <AppearanceSettingsSection />}
            {activeKey === 'privacy' && <PrivacySettingsSection />}
            {activeKey === 'security' && <SecuritySettingsSection />}
            {activeKey === 'integrations' && <IntegrationSettingsSection />}
          </div>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card className="bg-light">
            <Card.Body className="text-center">
              <h5 className="mb-3">Нужна помощь с настройками?</h5>
              <div className="d-flex justify-content-center gap-3">
                <Button variant="outline-primary">
                  <i className="bi bi-question-circle me-1"></i>
                  Документация
                </Button>
                <Button variant="outline-primary">
                  <i className="bi bi-chat-dots me-1"></i>
                  Служба поддержки
                </Button>
                <Button variant="outline-primary">
                  <i className="bi bi-lightbulb me-1"></i>
                  Предложить улучшение
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SettingsTab;